<template>
  <div class="schedule">
    <van-nav-bar fixed placeholder title="今日日程" />
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <van-list v-model="loading" :finished="finished" finished-text="没有更多了" @load="onLoadMore">
        <div class="list-content">
          <div class="schedule-card" v-for="item in scheduleList" :key="item._key">
            <div class="card-header">
              <div class="header-left">
                <div class="room-name">{{ item.room_name || '-' }} - {{ item.shop_name || '-' }}</div>
              </div>
              <div class="status-badge" :class="item._scheduleStatusClass">{{ item._scheduleStatusText }}</div>
            </div>
            <div class="card-body">
              <div class="info-row" v-if="item.shop_address">
                <van-icon name="location-o" size="16" color="#999" />
                <div class="info-text">地址：{{ item.shop_address }}</div>
              </div>
              <div class="info-row" v-if="item.start_time_str">
                <van-icon name="clock-o" size="16" color="#999" />
                <div class="info-text">预定开始时间：{{ item.room_booking_begin_time_str }}</div>
              </div>
              <div class="info-row" v-if="item.end_time_str">
                <van-icon name="clock-o" size="16" color="#999" />
                <div class="info-text">预定结束时间：{{ item.room_booking_end_time_str }}</div>
              </div>
            </div>
          </div>

          <van-empty v-if="!loading && scheduleList.length === 0" description="今日暂无日程" />
        </div>
      </van-list>
    </van-pull-refresh>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { daysOrderList } from '@/api/lxz/cleaner'

const pad2 = (n) => String(n).padStart(2, '0')
const getTodayKey = () => {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

const safeParseTime = (input) => {
  const raw = String(input || '').trim()
  if (!raw) return null
  const t = Date.parse(raw.replace(/-/g, '/'))
  if (!Number.isFinite(t)) return null
  return new Date(t)
}

const getScheduleStatus = (item) => {
  if (!item) return { text: '', cls: '' }
  if (item && item.status === 0) return { text: '未开始', cls: 'blue' }
  if (item && item.status === 1) return { text: '进行中', cls: 'orange' }
  if (item && item.status === 2) return { text: '提前结束', cls: 'gray' }
  if (item && item.status === 3) return { text: '已结束', cls: 'gray' }
  return { text: '未开始', cls: 'blue' }
}

const normalizeItem = (item) => {
  const start = item && (item.start_time_str || item.start_time || item.startTime || item.start)
  const end = item && (item.end_time_str || item.end_time || item.endTime || item.end)
  const id = item && (item.id || item.cleaner_log_id || item.cleanerLogId || item.order_id || item.orderId)
  return {
    ...item,
    id,
    start_time_str: start || '',
    end_time_str: end || ''
  }
}

export default {
  name: 'CleanerSchedule',
  data() {
    return {
      todayKey: getTodayKey(),
      scheduleList: [],
      loading: false,
      refreshing: false,
      fetching: false,
      finished: false,
      page: 1,
      pageSize: 10
    }
  },
  mounted() {
    this.reload()
  },
  methods: {
    handleToDetail(item) {
      if (!item || !item.id) return
      this.$router.push(`/cleanerOrder/order/${item.id}`)
    },
    async reload() {
      this.todayKey = getTodayKey()
      this.scheduleList = []
      this.finished = false
      this.page = 1
      await this.fetchMore(false)
    },
    async onRefresh() {
      this.refreshing = true
      await this.reload()
      this.refreshing = false
    },
    async onLoadMore() {
      await this.fetchMore(true)
    },
    mergeAndSort(nextItems) {
      const map = new Map()
      const all = this.scheduleList.concat(nextItems)
      for (let i = 0; i < all.length; i++) {
        const item = all[i]
        if (!item) continue
        const key = item.id != null ? String(item.id) : item._key
        if (!key) continue
        map.set(key, item)
      }
      const list = Array.from(map.values())
      list.sort((a, b) => {
        const ta = safeParseTime(a.start_time_str || a.create_time) || new Date(0)
        const tb = safeParseTime(b.start_time_str || b.create_time) || new Date(0)
        return ta.getTime() - tb.getTime()
      })
      this.scheduleList = list
    },
    decorateItem(item, source) {
      const normalized = normalizeItem(item)
      const s = getScheduleStatus(normalized)
      return {
        ...normalized,
        _source: source,
        _key: `${source}_${normalized && normalized.id != null ? normalized.id : Math.random().toString(16).slice(2)}`,
        _scheduleStatusText: s.text,
        _scheduleStatusClass: s.cls
      }
    },
    async fetchMore(isLoadMore) {
      if (this.fetching) return
      if (isLoadMore && this.finished) return

      this.fetching = true
      this.loading = true
      try {
        const targetPage = isLoadMore ? this.page + 1 : 1
        const res = await daysOrderList({ day: this.todayKey, page: targetPage })
        if (res && res.status === 200) {
          const list = (res.data && (res.data.list || res.data.data || res.data)) || { data: [] }
          const raw = Array.isArray(list.data) ? list.data : Array.isArray(list) ? list : []
          const decorated = raw.map((it) => this.decorateItem(it, 'daysOrder'))
          if (!isLoadMore) this.scheduleList = decorated
          else this.mergeAndSort(decorated)
          this.page = targetPage

          const serverLastPage = Number(list.last_page || list.lastPage || 0)
          if (serverLastPage > 0) {
            this.finished = this.page >= serverLastPage
          } else {
            const effectivePageSize = this.pageSize > 0 ? this.pageSize : 10
            this.finished = raw.length < effectivePageSize
          }
        } else {
          this.finished = true
        }
      } catch (e) {
        Toast.fail('获取今日日程失败')
        this.finished = true
      } finally {
        this.loading = false
        this.fetching = false
      }
    }
  }
}
</script>

<style scoped>
.schedule {
  min-height: 100vh;
  background: #f5f7fa;
}
.list-content {
  padding: 12px;
}
.schedule-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.header-left {
  display: flex;
  align-items: center;
  min-width: 0;
}
.room-name {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
}
.status-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  color: #fff;
  flex-shrink: 0;
}
.status-badge.blue {
  background: #1989fa;
}
.status-badge.orange {
  background: #ff9900;
}
.status-badge.gray {
  background: #ccc;
}
.card-body {
  margin-bottom: 4px;
}
.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.info-row:last-child {
  margin-bottom: 0;
}
.info-text {
  font-size: 13px;
  color: #666;
  margin-left: 8px;
}
</style>
